import { computed, defineComponent, onMounted, PropType, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { ProjectTypeAction, ProjectTypeGetter } from '@/store/projectType/projectType.module';
import { ProjectType } from '@/store/projectType/projectType.model';
import { ProjectSchema } from '@/store/project/project.validator';
import { ProjectForm } from '@/store/project/project.model';

export default defineComponent({
  name: 'ProjectFormComponent',
  props: {
    project: {
      type: Object as PropType<ProjectForm>,
      required: true,
    }
  },
  emits: [
    'validated'
  ],
  setup: (props, context) => {
    const store = useStore();
    const projectTypes = computed<ProjectType[]>(() => store.getters[ProjectTypeGetter.ALL]);
    const projectForm = reactive<ProjectForm>({
      name: props.project.name,
      description: props.project.description,
      projectTypeId: props.project.projectTypeId,
      fiscalYearId: props.project.fiscalYearId,
      provinceId: props.project.projectTypeId,
    });
    const validator = computed(() => ProjectSchema.validate(projectForm, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      projectTypeId: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'projectTypeId')),
    });
    const isValid = computed(() => !errors.name && !errors.projectTypeId);
    const submited = ref(false);

    onMounted(() => {
      store.dispatch(ProjectTypeAction.FETCH);
    });

    watch(() => props.project, (project) => {
      projectForm.name = project.name;
      projectForm.description = project.description;
      projectForm.projectTypeId = project.projectTypeId;
      projectForm.fiscalYearId = project.fiscalYearId;
      projectForm.provinceId = project.projectTypeId;
    });

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    const clearForm = () => {
      new Promise<void>((resolve) => {
        projectForm.name = props.project.name;
        projectForm.description = props.project.description;
        projectForm.projectTypeId = props.project.projectTypeId;
        projectForm.fiscalYearId = props.project.fiscalYearId;
        projectForm.provinceId = props.project.projectTypeId;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitForm = () => {
      submited.value = true;

      if (isValid.value) {
        context.emit('validated', projectForm);
      }
    };

    return {
      projectForm,
      projectTypes,
      errors,
      submited,
      submitForm,
      clearForm,
      unsubmit,
    };
  },
});
